import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Icon from 'src/components/Icon';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';
import { useTerms } from 'src/TermsContext';
import { useUser } from 'src/UserContext';

export default function useDrawer(): {
  openDrawer: () => void;
  DrawerComponent: JSX.Element;
} {
  const history = useCustomHistory();
  const { setIsTermShow, setIsPrivacyPolicyShow } = useTerms();
  const { t } = useTranslation();
  const { isLogin, userInfo, logout } = useUser();
  const drawerRef = React.useRef<HTMLDivElement | null>(null);
  const drawerVoidRef = React.useRef<HTMLDivElement | null>(null);
  const { showModal } = useModal();

  const openDrawer = useCallback(() => {
    document.querySelector('body')?.classList.add('overflow-hidden');
    if (drawerRef.current) drawerRef.current.classList.add('show');
    if (drawerVoidRef.current) drawerVoidRef.current.classList.add('show');
  }, [drawerRef, drawerVoidRef]);
  const closeDrawer = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      document.querySelector('body')?.classList.remove('overflow-hidden');
      if (drawerRef.current) drawerRef.current.classList.remove('show');
      if (drawerVoidRef.current) drawerVoidRef.current.classList.remove('show');
    },
    [drawerRef, drawerVoidRef],
  );

  const onClickLogout = useCallback((e) => {
    e.preventDefault();
    showModal({
      message: t('drawer/logout_confirm'),
      success: () => {
        closeDrawer(e);
        logout();
        location.reload();
      },
      fail: () => {
        return;
      },
    });
  }, []);

  return {
    openDrawer,
    DrawerComponent: (
      <>
        <div ref={drawerRef} className="drawer">
          <div className="drawer-content overflow-y-auto">
            <div className="flex justify-end items-center mt-4">
              <div className="mr-6 cursor-pointer" onClick={closeDrawer}>
                <Icon name="close" size="32" />
              </div>
            </div>
            {isLogin && (
              <div className="N_20_R text-black mt-9.5 ml-6">
                <div className="">{t('drawer/title')}</div>
                <div className="flex justify-between items-center mt-1">
                  <div className="N_16_M text-greyish-brown">{userInfo.email}</div>
                  <div
                    className="mr-6 cursor-pointer"
                    onClick={(e) => {
                      closeDrawer(e);
                      history.push('/user');
                    }}
                  >
                    <Icon name="setting" size="20" />
                  </div>
                </div>
              </div>
            )}
            {!isLogin && (
              <div className="N_20_R text-black mt-12 ml-6 w-40">
                <Trans
                  i18nKey="drawer/login_guide_title"
                  components={{
                    a: (
                      <span
                        className="text-bluey-purple underline cursor-pointer"
                        onClick={(e) => {
                          closeDrawer(e);
                          history.push('/login');
                        }}
                      />
                    ),
                  }}
                ></Trans>
              </div>
            )}
            <div className="mt-16 N_16_R text-black">
              <div
                className="py-4 px-6 flex items-center cursor-pointer"
                onClick={(e) => {
                  if (!isLogin) {
                    showModal({
                      title: '',
                      message: t('drawer/login_error'),
                      success: () => {
                        closeDrawer(e);
                        history.push('/login', {
                          afterLoginEndpoint: '/product/my',
                        });
                      },
                      fail: () => {
                        console.log('fail');
                      },
                    });
                  } else {
                    closeDrawer(e);
                    history.push('/product/my');
                  }
                }}
              >
                <Icon name="ticket_b" size="32" />
                <div className="ml-4">{t('product/my/title')}</div>
              </div>
              <div
                className="py-4 px-6 flex items-center cursor-pointer"
                onClick={(e) => {
                  closeDrawer(e);
                  history.push('/product/purchase');
                }}
              >
                <Icon name="buy-k" size="32" />
                <div className="ml-4">{t('product/purchase/title')}</div>
              </div>
              <div
                className="py-4 px-6 flex items-center cursor-pointer"
                onClick={(e) => {
                  closeDrawer(e);
                  history.push('/notice');
                }}
              >
                <Icon name="notice" size="32" />
                <div className="ml-4">{t('notice/title')}</div>
              </div>
              <div
                className="py-4 px-6 flex items-center cursor-pointer"
                onClick={(e: any) => {
                  closeDrawer(e);
                  history.push('/customer');
                }}
              >
                <Icon name="customer" size="32" />
                <div className="ml-4">{t('customer/title')}</div>
              </div>
            </div>
            <div className="bottom-menu fixed bottom-0 w-full bg-white">
              <div className="N_12_R text-greyish-brown flex justify-between items-center h-18 border-t border-ice-blue">
                <div className="flex items-center">
                  <div
                    className="ml-6 cursor-pointer"
                    onClick={(e) => {
                      closeDrawer(e);
                      setIsTermShow(true);
                    }}
                  >
                    {t('drawer/terms_title')}
                  </div>
                  <span className="division inline-block bg-ice-blue mx-2" />
                  <div
                    className="cursor-pointer"
                    onClick={(e) => {
                      closeDrawer(e);
                      setIsPrivacyPolicyShow(true);
                    }}
                  >
                    {t('drawer/privacy_policy_title')}
                  </div>
                </div>
                {isLogin && (
                  <div className="N_12_M text-black underline mr-6 cursor-pointer" onClick={onClickLogout}>
                    {t('drawer/logout_title')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div ref={drawerVoidRef} className="drawer-void" onClick={closeDrawer}></div>
      </>
    ),
  };
}
